import { useState } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate, useSearchParams } from "react-router-dom";
import { safeRedirect } from "@/lib/safeRedirect";

import { z } from "zod";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { MobileShell } from "@/components/MobileShell";
import { BrandHeader } from "@/components/BrandHeader";
import { toast } from "sonner";
import { Loader2, Mail, Lock, User as UserIcon, AtSign } from "lucide-react";

const signupSchema = z.object({
  displayName: z.string().trim().min(2, "Name must be at least 2 characters").max(60),
  cashtag: z.string().trim().min(3, "Cashtag must be 3+ chars").max(20).regex(/^[a-zA-Z0-9_]+$/, "Letters, numbers, _ only"),
  email: z.string().trim().email("Invalid email").max(255),
  password: z.string().min(8, "Password must be 8+ characters").max(72),
});

const loginSchema = z.object({
  email: z.string().trim().email("Invalid email"),
  password: z.string().min(1, "Password required"),
});

const Auth = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const next = safeRedirect(searchParams.get("next"), "/wallet");
  const [tab, setTab] = useState<"signup" | "login">("signup");
  const [loading, setLoading] = useState(false);


  // signup state
  const [displayName, setDisplayName] = useState("");
  const [cashtag, setCashtag] = useState("");
  const [emailS, setEmailS] = useState("");
  const [passwordS, setPasswordS] = useState("");

  // login state
  const [emailL, setEmailL] = useState("");
  const [passwordL, setPasswordL] = useState("");

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    const parsed = signupSchema.safeParse({ displayName, cashtag, email: emailS, password: passwordS });
    if (!parsed.success) {
      toast.error(parsed.error.issues[0].message);
      return;
    }
    setLoading(true);
    const { error } = await supabase.auth.signUp({
      email: parsed.data.email,
      password: parsed.data.password,
      options: {
        emailRedirectTo: `${window.location.origin}${next}`,
        data: {
          display_name: parsed.data.displayName,
          cashtag: parsed.data.cashtag.toLowerCase(),
        },
      },
    });
    setLoading(false);
    if (error) {
      toast.error(error.message.includes("already") ? "Account already exists. Try logging in." : error.message);
      return;
    }
    toast.success("Welcome to Cash Isle! 🌴");
    navigate(next, { replace: true });
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    const parsed = loginSchema.safeParse({ email: emailL, password: passwordL });
    if (!parsed.success) {
      toast.error(parsed.error.issues[0].message);
      return;
    }
    setLoading(true);
    const { error } = await supabase.auth.signInWithPassword({
      email: parsed.data.email,
      password: parsed.data.password,
    });
    setLoading(false);
    if (error) {
      toast.error("Invalid email or password");
      return;
    }
    toast.success("Welcome back!");
    navigate(next, { replace: true });
  };

  return (
    <MobileShell>
      <Helmet>
        <title>Sign in or sign up — Cash Isle</title>
        <meta name="description" content="Sign in to Cash Isle or create an account to send, receive, and grow money across the islands." />
        <link rel="canonical" href="https://cash-isle-hustle-hub.lovable.app/auth" />
        <meta property="og:title" content="Sign in or sign up — Cash Isle" />
        <meta property="og:description" content="Sign in to Cash Isle or create an account to send, receive, and grow money across the islands." />
        <meta property="og:url" content="https://cash-isle-hustle-hub.lovable.app/auth" />
      </Helmet>
      <BrandHeader onBack={() => navigate("/")} curve />
      <div className="-mt-6 mx-4 bg-background rounded-2xl shadow-card px-6 pt-7 pb-8 flex-1 animate-fade-in-up">
        <Tabs value={tab} onValueChange={(v) => setTab(v as "signup" | "login")} className="w-full">
          <TabsList className="grid grid-cols-2 w-full mb-6">
            <TabsTrigger value="signup">Sign Up</TabsTrigger>
            <TabsTrigger value="login">Log In</TabsTrigger>
          </TabsList>

          <TabsContent value="signup">
            <h1 className="font-display text-3xl font-bold text-center mb-2">Sign Up</h1>
            <p className="text-center text-muted-foreground text-sm mb-6">
              Create your account in just a few steps to start earning and transfer money easily.
            </p>
            <form onSubmit={handleSignup} className="space-y-3">
              <Field icon={UserIcon} placeholder="Full Name" value={displayName} onChange={setDisplayName} />
              <Field icon={AtSign} placeholder="Cashtag (e.g. yourname)" value={cashtag} onChange={(v) => setCashtag(v.replace(/[^a-zA-Z0-9_]/g, ""))} prefix="$" />
              <Field icon={Mail} type="email" placeholder="Email" value={emailS} onChange={setEmailS} />
              <Field icon={Lock} type="password" placeholder="Password" value={passwordS} onChange={setPasswordS} />
              <Button type="submit" variant="hero" size="xl" className="w-full mt-4" disabled={loading}>
                {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Sign Up"}
              </Button>
              <p className="text-center text-sm text-muted-foreground pt-2">
                Already have an account?{" "}
                <button type="button" onClick={() => setTab("login")} className="text-primary font-semibold hover:underline">Log In</button>
              </p>
            </form>
          </TabsContent>

          <TabsContent value="login">
            <h1 className="font-display text-3xl font-bold text-center mb-2">Welcome back</h1>
            <p className="text-center text-muted-foreground text-sm mb-6">Log in to keep your hustle moving.</p>
            <form onSubmit={handleLogin} className="space-y-3">
              <Field icon={Mail} type="email" placeholder="Email" value={emailL} onChange={setEmailL} />
              <Field icon={Lock} type="password" placeholder="Password" value={passwordL} onChange={setPasswordL} />
              <div className="text-right">
                <button type="button" onClick={() => navigate("/forgot-password")} className="text-xs text-primary font-medium hover:underline">
                  Forgot password?
                </button>
              </div>
              <Button type="submit" variant="hero" size="xl" className="w-full mt-4" disabled={loading}>
                {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Log In"}
              </Button>
              <p className="text-center text-sm text-muted-foreground pt-2">
                New to Cash Isle?{" "}
                <button type="button" onClick={() => setTab("signup")} className="text-primary font-semibold hover:underline">Sign Up</button>
              </p>
            </form>
          </TabsContent>
        </Tabs>
      </div>
    </MobileShell>
  );
};

const Field = ({
  icon: Icon,
  prefix,
  ...props
}: {
  icon: React.ComponentType<{ className?: string }>;
  prefix?: string;
  type?: string;
  placeholder: string;
  value: string;
  onChange: (v: string) => void;
}) => (
  <div className="relative">
    <Icon className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
    {prefix && (
      <span className="absolute left-11 top-1/2 -translate-y-1/2 text-muted-foreground font-medium">{prefix}</span>
    )}
    <Input
      type={props.type ?? "text"}
      placeholder={props.placeholder}
      value={props.value}
      onChange={(e) => props.onChange(e.target.value)}
      className={`h-13 pl-12 ${prefix ? "pl-[3.25rem]" : ""} h-12 rounded-xl bg-secondary/60 border-transparent focus-visible:bg-background focus-visible:border-primary`}
      style={prefix ? { paddingLeft: "2.6rem" } : undefined}
    />
  </div>
);

export default Auth;
